import { type ChangeEvent, type FormEvent } from 'react'

import { Field } from '#app/components/form/field.tsx'
import { SelectField } from '#app/components/form/select-field.tsx'
import { YesNoField } from '#app/components/form/yes-no-field.tsx'

import { TRAINING_PROGRAM_COPY } from '#app/routes/mocra+/_form+/components/training-programs/constants.ts'
import {
	type TrainingProgramDraft,
	type TrainingAudienceValue,
	type TrainingCadenceValue,
	type TrainingDeliveryValue,
} from '#app/routes/mocra+/_form+/components/training-programs/types.ts'
import {
	TRAINING_AUDIENCE_OPTIONS,
	TRAINING_DELIVERY_OPTIONS,
	TRAINING_FREQUENCY_OPTIONS,
} from '#app/routes/mocra+/_form+/intake-options.ts'

type DraftErrors = Partial<Record<keyof TrainingProgramDraft, string>>

type Props = {
	readonly formId: string
	readonly draft: TrainingProgramDraft
	readonly errors: DraftErrors
	readonly onDraftFieldChange: <Field extends keyof TrainingProgramDraft>(
		field: Field,
		value: TrainingProgramDraft[Field],
	) => void
	readonly onSubmit: (event: FormEvent<HTMLFormElement>) => void
}

export function TrainingProgramForm({
	formId,
	draft,
	errors,
	onDraftFieldChange,
	onSubmit,
}: Props) {
	const handleAudienceChange = (event: ChangeEvent<HTMLSelectElement>) => {
		onDraftFieldChange('audience', event.target.value as TrainingAudienceValue)
	}

	const handleDeliveryChange = (event: ChangeEvent<HTMLSelectElement>) => {
		onDraftFieldChange('delivery', event.target.value as TrainingDeliveryValue)
	}

	const handleCadenceChange = (event: ChangeEvent<HTMLSelectElement>) => {
		onDraftFieldChange('cadence', event.target.value as TrainingCadenceValue)
	}

	const handlePlatformChange = (event: ChangeEvent<HTMLInputElement>) => {
		onDraftFieldChange('platform', event.target.value)
	}

	const handleTracksCompletionChange = (value: boolean) => {
		onDraftFieldChange('tracksCompletion', value)
	}

	return (
		<form id={formId} onSubmit={onSubmit}>
			<SelectField
				labelProps={{ children: TRAINING_PROGRAM_COPY.fields.audienceLabel }}
				selectProps={{
					value: draft.audience,
					onChange: handleAudienceChange,
					onValueChange: (next) =>
						onDraftFieldChange('audience', next as TrainingAudienceValue),
				}}
				options={TRAINING_AUDIENCE_OPTIONS}
			/>
			<SelectField
				labelProps={{ children: TRAINING_PROGRAM_COPY.fields.deliveryLabel }}
				selectProps={{
					value: draft.delivery,
					onChange: handleDeliveryChange,
					onValueChange: (next) =>
						onDraftFieldChange('delivery', next as TrainingDeliveryValue),
				}}
				options={TRAINING_DELIVERY_OPTIONS}
			/>
			<SelectField
				labelProps={{ children: TRAINING_PROGRAM_COPY.fields.cadenceLabel }}
				selectProps={{
					value: draft.cadence,
					onChange: handleCadenceChange,
					onValueChange: (next) =>
						onDraftFieldChange('cadence', next as TrainingCadenceValue),
				}}
				options={TRAINING_FREQUENCY_OPTIONS}
			/>
			<Field
				labelProps={{ children: TRAINING_PROGRAM_COPY.fields.platformLabel }}
				inputProps={{
					value: draft.platform,
					onChange: handlePlatformChange,
					type: 'text',
				}}
				errors={errors.platform ? [errors.platform] : undefined}
			/>
			<YesNoField
				labelProps={{
					children: TRAINING_PROGRAM_COPY.fields.tracksCompletionLabel,
				}}
				yesProps={{
					checked: draft.tracksCompletion,
					onChange: () => handleTracksCompletionChange(true),
					name: 'tracksCompletion',
				}}
				noProps={{
					checked: !draft.tracksCompletion,
					onChange: () => handleTracksCompletionChange(false),
					name: 'tracksCompletion',
				}}
			/>
		</form>
	)
}
